'use client';

/**
 * Offers Hook
 * 
 * Provides offer management functionality for customers.
 * Uses the CustomerAuthProvider for API access.
 */

import { useState, useCallback, useEffect } from 'react';
import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import type { Offer, CreateOfferRequest } from '@/types/customer';

// ============================================================================
// Types
// ============================================================================

export interface UseOffersReturn {
  /** All offers for the current customer */
  offers: Offer[];
  /** Whether offers are loading */
  isLoading: boolean;
  /** Error if any */
  error: string | null;
  /** Refresh offers */
  refresh: () => Promise<void>;
  /** Get a specific offer */
  getOffer: (offerId: string) => Promise<Offer | null>;
  /** Create a new offer */
  createOffer: (data: CreateOfferRequest) => Promise<Offer | null>;
  /** Update a draft offer */
  updateOffer: (offerId: string, data: Partial<CreateOfferRequest>) => Promise<Offer | null>;
  /** Submit an offer */
  submitOffer: (offerId: string) => Promise<Offer | null>;
  /** Withdraw an offer */
  withdrawOffer: (offerId: string, reason?: string) => Promise<Offer | null>;
  /** Delete a draft offer */
  deleteOffer: (offerId: string) => Promise<boolean>;
}

// ============================================================================
// Hook
// ============================================================================

export function useOffers(): UseOffersReturn {
  const { getCustomerClient, isAuthenticated } = useCustomerAuth();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch offers
  const refresh = useCallback(async () => {
    const client = getCustomerClient();
    if (!client) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await client.getOffers();
      setOffers(response?.items || []);
    } catch (err) {
      // Log the error but handle gracefully for API issues
      const errorMessage = err instanceof Error ? err.message : 'Failed to load offers';
      console.error('[useOffers] Failed to fetch offers:', err);
      
      // Don't show error to user for API availability issues
      if (errorMessage.includes('API server unavailable') || 
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('Network')) {
        setOffers([]);
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [getCustomerClient]);

  // Load offers when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      refresh();
    } else {
      setOffers([]);
    }
  }, [isAuthenticated, refresh]);

  // Get a specific offer
  const getOffer = useCallback(async (offerId: string): Promise<Offer | null> => {
    const client = getCustomerClient();
    if (!client) return null;

    try {
      return await client.getOffer(offerId);
    } catch (err) {
      console.error('[useOffers] Failed to get offer:', err);
      return null;
    }
  }, [getCustomerClient]);

  // Create a new offer
  const createOffer = useCallback(async (data: CreateOfferRequest): Promise<Offer | null> => {
    const client = getCustomerClient();
    if (!client) return null;

    try {
      const offer = await client.createOffer(data);
      setOffers((prev) => [offer, ...prev]);
      return offer;
    } catch (err) {
      console.error('[useOffers] Failed to create offer:', err);
      setError(err instanceof Error ? err.message : 'Failed to create offer');
      return null;
    }
  }, [getCustomerClient]);

  // Update a draft offer
  const updateOffer = useCallback(async (
    offerId: string,
    data: Partial<CreateOfferRequest>
  ): Promise<Offer | null> => {
    const client = getCustomerClient();
    if (!client) return null;

    try {
      const offer = await client.updateOffer(offerId, data);
      setOffers((prev) => prev.map((o) => (o.id === offerId ? offer : o)));
      return offer;
    } catch (err) {
      console.error('[useOffers] Failed to update offer:', err);
      setError(err instanceof Error ? err.message : 'Failed to update offer');
      return null;
    }
  }, [getCustomerClient]);

  // Submit an offer
  const submitOffer = useCallback(async (offerId: string): Promise<Offer | null> => {
    const client = getCustomerClient();
    if (!client) return null;

    try {
      const offer = await client.submitOffer(offerId);
      setOffers((prev) => prev.map((o) => (o.id === offerId ? offer : o)));
      return offer;
    } catch (err) {
      console.error('[useOffers] Failed to submit offer:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit offer');
      return null;
    }
  }, [getCustomerClient]);

  // Withdraw an offer
  const withdrawOffer = useCallback(async (
    offerId: string,
    reason?: string
  ): Promise<Offer | null> => {
    const client = getCustomerClient();
    if (!client) return null;

    try {
      const offer = await client.withdrawOffer(offerId, reason);
      setOffers((prev) => prev.map((o) => (o.id === offerId ? offer : o)));
      return offer;
    } catch (err) {
      console.error('[useOffers] Failed to withdraw offer:', err);
      setError(err instanceof Error ? err.message : 'Failed to withdraw offer');
      return null;
    }
  }, [getCustomerClient]);

  // Delete a draft offer
  const deleteOffer = useCallback(async (offerId: string): Promise<boolean> => {
    const client = getCustomerClient();
    if (!client) return false;

    try {
      await client.deleteOffer(offerId);
      setOffers((prev) => prev.filter((o) => o.id !== offerId));
      return true;
    } catch (err) {
      console.error('[useOffers] Failed to delete offer:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete offer');
      return false;
    }
  }, [getCustomerClient]);

  return {
    offers,
    isLoading,
    error,
    refresh,
    getOffer,
    createOffer,
    updateOffer,
    submitOffer,
    withdrawOffer,
    deleteOffer,
  };
}

export default useOffers;
